import { Component, Input, OnInit } from "@angular/core";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatSortModule } from "@angular/material/sort";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { AddEditAppComponent } from "./add-edit-app/add-edit-app.component";
import { CommonModule } from "@angular/common";
import { UserTypeEnum } from "src/app/shared/common/common.enum";
import { GlobalServiceService } from "src/app/shared/services/global-service.service";
import { MatSidenav } from "@angular/material/sidenav";
import { MatIconModule } from "@angular/material/icon";
import { AdminService, AppData } from "src/app/api/admin.service";
import { GenericApiResponse } from "src/app/shared/common/api-response.model";
import { FlyoutService } from "src/app/shared/services/flyout.service";

export interface GroupRow {
  isGroup: true;
  level: 1;
  app?: string;
  appType?: string;
  expanded?: boolean;
}

export type TableRow = GroupRow | AppData;

@Component({
  selector: "app-admin-dashboard",
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    AddEditAppComponent,
    MatIconModule,
  ],
  templateUrl: "./admin-dashboard.component.html",
  styleUrl: "./admin-dashboard.component.css",
})
export class AdminDashboardComponent implements OnInit {
  @Input() sidenav!: MatSidenav;
  dataSource!: MatTableDataSource<AppData>;
  appData!: AppData[];
  data!: AppData[];
  // Flattened result: groups + items
  rows: TableRow[] = [];
  isMobile = false;
  displayedColumns: string[] = [];
  dataSourceUI = new MatTableDataSource<TableRow>([]);

  constructor(
    private gbService: GlobalServiceService,
    private adminService: AdminService,
    private flyoutService: FlyoutService
  ) {
    this.isMobile = this.gbService.isMobile;

    this.displayedColumns = this.isMobile
      ? ["Type", "Placement", "Status", "CPAMAppId"]
      : ["Type", "Placement", "Status", "CPAMAppId", "OktaSsoUrl"]; // columns for leaf rows (customize)
  }
  isResetFlyout(): boolean {
    if (!this.flyoutService.isResetFlyout()) {
      this.appData = [];
    }

    return (
      this.flyoutService.isResetFlyout() &&
      (!this.appData || this.appData.length === 0)
    );
  }
  ngOnInit(): void {
    this.getAppData();
  }

  getAppData() {
    this.adminService
      .getApplications(0)
      .subscribe((response: GenericApiResponse<AppData[]>) => {
        if (response.data && response.data.length > 0) {
          this.data = response.data;
          this.rows = this.buildGroupedRows(response.data);
          this.dataSourceUI.data = this.rows;
        }
      });
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSourceUI.filter = filterValue.trim().toLowerCase();

    if (this.dataSourceUI.paginator) {
      this.dataSourceUI.paginator.firstPage();
    }
  }

  onRowClick(app: AppData, isNewApp: boolean) {
    if (isNewApp) {
      app = { isActive: true } as AppData;
    }
    const rows = !isNewApp
      ? this.data.filter((ele: AppData) => {
          return ele.appName === app.appName;
        })
      : [
          { isActive: true, type: UserTypeEnum.internal } as AppData,
          { isActive: true, type: UserTypeEnum.external } as AppData,
        ];
    this.flyoutService.setFlyoutHeader(`${app.appName ?? ""}`);

    this.appData = rows;
  }

  onCancelHandle(value: string) {
    this.flyoutService.setFlyoutHeader(value);
    this.appData = [];
  }

  onDataRefresh(event: Event) {
    this.getAppData();
  }

  // new table

  private buildGroupedRows(items: AppData[]): TableRow[] {
    const result: TableRow[] = [];

    // Group by app
    const byApp = new Map<string, AppData[]>();
    for (const item of items) {
      const list = byApp.get(item.appName) ?? [];
      list.push(item);
      byApp.set(item.appName, list);
    }

    // For each app, push a group row then its items
    for (const [app, appItems] of byApp.entries()) {
      const appGroup: GroupRow = {
        isGroup: true,
        level: 1,
        app,
        expanded: true,
      };
      result.push(appGroup);
      for (const item of appItems) {
        result.push(item);
      }
    }
    return result;
  }

  // Helpers for templates

  isGroupRow = (_index: number, row: TableRow): boolean =>
    (row as any).isGroup === true;

  isLevel1 = (_index: number, row: TableRow): boolean => {
    const g = row as any;
    return g.isGroup === true && g.level === 1;
  };

  isLeafRow = (index: number, row: TableRow): boolean =>
    !this.isGroupRow(index, row);

  toggleGroup(row: GroupRow): void {
    row.expanded = !row.expanded;

    // Show/hide descendants of this group
    const startIndex = this.rows.indexOf(row);
    if (startIndex < 0) {
      return;
    }

    // determine how far to hide: until next group of same or higher level
    const targetLevel = row.level;

    // If collapsing, hide; if expanding, show
    const collapsing = !row.expanded;

    for (let i = startIndex + 1; i < this.rows.length; i++) {
      const r = this.rows[i];

      // Stop at next group of same or higher level
      if ((r as GroupRow).isGroup && (r as GroupRow).level <= targetLevel) {
        break;
      }

      // For level1 group: affect both level2 group and leaf items below it
      // For level2 group: affect only the leaf items below it until the next group
      (r as any).__hiddenByParent = collapsing
        ? (r as any).__hiddenByParentLevel ?? targetLevel
        : undefined;
      // For expansion, remove hidden flag only if it was hidden by this exact parent level
      if (!collapsing && (r as any).__hiddenByParentLevel === targetLevel) {
        (r as any).__hiddenByParent = undefined;
        (r as any).__hiddenByParentLevel = undefined;
      }
      if (collapsing) {
        (r as any).__hiddenByParentLevel = targetLevel;
      }
    }

    // Reapply filter to reflect hidden flags (MatTable doesn’t have row-level hide; we filter data)
    this.applyHiddenFilter();
  }

  applyHiddenFilter(): void {
    // Filter rows by hidden flag
    const visible = this.rows.filter((r) => !(r as any).__hiddenByParent);
    this.dataSourceUI.data = visible;
  }

  /** Find the first leaf item under a given level-1 group row */
  private getFirstLeafUnderGroup(group: GroupRow): AppData | undefined {
    const start = this.rows.indexOf(group);
    if (start < 0) return;

    // Scan until next group of same/higher level
    for (let i = start + 1; i < this.rows.length; i++) {
      const r = this.rows[i] as any;
      if (r.isGroup && r.level <= group.level) break; // next sibling group
      if (!r.isGroup) return r as AppData; // first leaf encountered
    }
    return undefined;
  }

  onPrimaryLeafClick(group: GroupRow) {
    const firstLeaf = this.getFirstLeafUnderGroup(group);
    if (!firstLeaf) return;
    // Do your action on the first leaf item
    this.onRowClick(firstLeaf, false);
  }
}
